import { RootState } from '../app/state'
import { AnswerId, Answers, Scenario, ScenarioType, StepId, Steps } from './interfaces'
import { filterPhones } from '../filters/phone-filters'
import { createSelector } from '@reduxjs/toolkit'
import { pickBy } from 'lodash'
import { PhoneSpecs, PhoneSpecsCondensed } from '../phones'
import { AnswersState } from './answers-slice'
import { filterPhonesCondensed } from '../filters/phone-filters-condensed'

const selectAnswersState = (state: RootState) => state.answers
const selectStepState = (state: RootState) => state.step
export const selectProgressState = (state: RootState) => state.progress.value
export const selectScenario = (state: RootState): Scenario | null => state.scenario.current
export const selectOriginalScenarioType = (state: RootState): ScenarioType => state.scenario.originalScenarioType
export const selectPhones = (state: RootState): PhoneSpecs[] => state.phones
export const selectPhonesCondensed = (state: RootState): PhoneSpecsCondensed[] => state.phonesCondensed
export const selectReferrerUrl = (state: RootState) => state.referrerUrl
export const selectRedirectionUrl = (state: RootState) => state.redirectionUrl
export const selectSearchText = (state: RootState) => state.searchText

const normalizeAnswersState = (answers: AnswersState) => {
  const map: Answers = {}

  Object.entries(answers).forEach(([stepId, answer]) => {
    map[stepId as Steps] = answer.answerIds
  })

  return map
}

export const selectAnswers = createSelector([selectAnswersState], (answers): Answers => normalizeAnswersState(answers))
export const selectPrefilledAnswers = createSelector(
  [selectAnswersState],
  (answers): Answers => normalizeAnswersState(pickBy(answers, (value) => value.prefilled)),
)
export const selectScenarioId = createSelector([selectScenario], (scenario): string | undefined => scenario?.id)
export const selectScenarioType = createSelector([selectScenario], (scenario): string | undefined => scenario?.type)
export const selectMilestones = createSelector(
  [selectScenario],
  (scenario): string[] | undefined => scenario?.milestones,
)

export const selectCurrentStepId = createSelector([selectStepState], (step): StepId | undefined => step.current)
export const selectScenarioIsLoading = (state: RootState): boolean => {
  return state.scenario.loading
}

export const selectScenarioIsWaitingForAnswer = (state: RootState): boolean => {
  return state.scenario.waitingForAnswer
}

export const selectScenarioAdditionalInformation = (state: RootState): Record<string, unknown> =>
  state.scenario.additionalInformation

export const selectScenarioCookie = (state: RootState): Record<string, unknown> => state.scenario.scenarioCookie

export const selectScenarioScenarioType = (state: RootState): string => state.scenario.scenarioScenarioType

export const selectProvince = createSelector([selectAnswers], (answers): string | null =>
  answers[Steps.PROVINCE] ? answers[Steps.PROVINCE]![0] : null,
)

export const selectCurrentStep = createSelector([selectScenario, selectCurrentStepId], (scenario, stepId) =>
  scenario?.steps.find((step) => step.id === stepId),
)

export const selectIsCurrentStepPrefilled = createSelector(
  [selectPrefilledAnswers, selectCurrentStepId],
  (prefilledAnswers, currentStepId): boolean =>
    currentStepId !== undefined && prefilledAnswers[currentStepId] !== undefined,
)

export const selectHasPreviousStep = createSelector([selectStepState], (step) => {
  return step.previous.length > 0
})

export const selectHasNextStep = createSelector([selectStepState], (step) => {
  return step.next.length > 0
})

export const selectNextSteps = createSelector([selectStepState], (step) => step.next)

export const selectNextStepId = createSelector(
  [selectScenario, selectCurrentStepId, selectAnswers],
  (scenario, currentStepId, answers): StepId | null => {
    const step = scenario?.steps.find((step) => step.id === currentStepId)

    if (step && currentStepId) {
      const answerIds = answers[currentStepId] ?? null
      const answer = step.answers.find((answer) => answer.id === (answerIds ? answerIds[0] : null))

      if (answer) {
        return answer.next || step.next || null
      }

      return step.next || null
    }

    return null
  },
)

const selectScenarioIndex = createSelector(
  [selectScenario, selectCurrentStepId],
  (scenario, currentStepId): number | undefined => {
    return scenario?.steps.findIndex((step) => step.id === currentStepId)
  },
)

export const selectIsScenarioStarted = createSelector([selectScenarioIndex], (scenarioIndex): boolean => {
  return scenarioIndex !== undefined && scenarioIndex > 0
})

export const selectIsLastStep = createSelector(
  [selectScenario, selectCurrentStepId],
  (scenario, currentStepId): boolean => {
    const index = scenario?.steps.findIndex((step) => step.id === currentStepId)

    if (index === undefined || index < 0) return false
    return scenario?.steps.length === index + 1
  },
)

export const selectCurrentAnswers = createSelector(
  [selectCurrentStepId, selectAnswers],
  (currentStepId, answers): AnswerId[] => {
    return currentStepId && answers[currentStepId] ? answers[currentStepId]! : []
  },
)

export const selectFilteredPhones = createSelector([selectPhones, selectAnswers], (phones, answers) => {
  if (!Object.keys(answers).length) {
    return phones
  }
  return filterPhones(phones, answers)
})

export const selectFilteredPhonesCondensed = createSelector(
  [selectPhonesCondensed, selectAnswers],
  (phones, answers) => {
    if (!Object.keys(answers).length) {
      return phones
    }
    return filterPhonesCondensed(phones, answers)
  },
)

export const selectHasFilteredPhones = createSelector(
  [selectFilteredPhones],
  (filteredPhones) => filteredPhones.length > 0,
)
